import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import styles from '../styles/anomaly-breakdown.module.scss'
import SimpleCarousel from './simple-carousel'
import Loader from './loader'
import { updateAnomalyDescription } from '../context/actions'
import { returnUnique } from '../helpers'

export default function CausesAndSolutionsCarousels({
  viewOnly = false,
}: {
  viewOnly?: boolean
}): React.ReactElement {
  const dispatch = useDispatch()
  const [causeCarouselActive, setCauseCarouselActive] = useState(0)
  const [solutionCarouselActive, setSolutionCarouselActive] = useState(0)

  const { anomaly, causes } = useSelector((s: any): any => s)

  const { dislikedActions, dislikedCauses, likedActions, likedCauses } =
    anomaly?.description || {}

  const { recommendedCauses, loading } = causes || {}

  useEffect(() => {
    setCauseCarouselActive(0)
    setSolutionCarouselActive(0)
  }, [recommendedCauses])

  if (loading) {
    return (
      <>
        <Loader height="30px" />
        <Loader height="60px" />
        <Loader height="30px" />
        <Loader height="60px" />
      </>
    )
  }

  if (
    !recommendedCauses ||
    (recommendedCauses && recommendedCauses.length === 0)
  ) {
    return (
      <>
        <p>
          No possible causes available for the dimensions selected - please add
          your own above
        </p>
      </>
    )
  }

  const updateStatus = (anom: any) => {
    if (anomaly && anomaly.description !== null) {
      const data = { ...anomaly.description, ...anom }
      dispatch(
        updateAnomalyDescription({
          ...data,
        }),
      )
    }
  }

  return (
    <SimpleCarousel
      viewOnly={viewOnly}
      defaultActive={causeCarouselActive}
      onClickHelpful={(index) => {
        const { causeID } = recommendedCauses[index]
        updateStatus({
          likedCauses: returnUnique([...likedCauses, causeID]),
        })
      }}
      onClickNotHelpful={(index) => {
        const { causeID } = recommendedCauses[index]
        updateStatus({
          dislikedCauses: returnUnique([...dislikedCauses, causeID]),
        })
      }}
      title="Possible causes"
      onChangeSlide={(slide) => {
        setSolutionCarouselActive(0)
        setCauseCarouselActive(slide)
      }}
      items={recommendedCauses.map((item) =>
        item ? (
          <section aria-label="solution" key={item.causeID}>
            <article className={styles.carouselBody}>
              <p>{item.cause}</p>
            </article>
            <SimpleCarousel
              viewOnly={viewOnly}
              defaultActive={solutionCarouselActive}
              title="Possible solutions"
              onClickHelpful={(index) => {
                const { causeID } = recommendedCauses[index]
                updateStatus({
                  likedActions: returnUnique([...likedActions, causeID]),
                })
              }}
              onClickNotHelpful={(index) => {
                const { causeID } = recommendedCauses[index]
                updateStatus({
                  dislikedActions: returnUnique([...dislikedActions, causeID]),
                })
              }}
              onChangeSlide={(slide) => {
                setSolutionCarouselActive(slide)
              }}
              items={item.actions.map((action) =>
                item ? (
                  <section aria-label="solution">
                    <article className={styles.carouselBody}>
                      <p>{action.action}</p>
                    </article>
                  </section>
                ) : null,
              )}
            />
          </section>
        ) : null,
      )}
    />
  )
}
